import React, { useState } from "react";
import Swal from "sweetalert2";
import toastr from "toastr";
import {
    removeRequisitionFile,
    uploadRequisitionFiles
} from "../../../services/clinicPortalServices/orderSearchService";
import { serviceConstants } from "../../../services/common/constants";
import { getUserAuthToken } from "../../../services/common/util";

const UploadFileModel = ({
    isOpen,
    onClose,
    orderId,
    requisitionNo,
    requisitionPdfPaths,
    setRequisitionPdfPaths,
    onFilesChanged
}) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [deletingFile, setDeletingFile] = useState("");

    const updateFiles = (paths) => {
        setRequisitionPdfPaths(paths);
        if (onFilesChanged) {
            onFilesChanged();
        }
    };

    const handleFileChange = (event) => {
        setSelectedFiles(Array.from(event.target.files));
    };

    const handleUpload = async () => {
        if (!selectedFiles.length) {
            toastr.warning("Please select files to upload.");
            return;
        }

        const formData = new FormData();
        selectedFiles.forEach((file) => formData.append("files", file));
        formData.append("requisitionPdfPath", requisitionNo);
        formData.append("fileNames", JSON.stringify(selectedFiles.map((file) => file.name)));

        setIsUploading(true);
        try {
            const result = await uploadRequisitionFiles(formData, requisitionNo);
            if (result && result.status === 200 && Array.isArray(result.requisition_pdf_paths)) {
                updateFiles(result.requisition_pdf_paths);
                setSelectedFiles([]);
                toastr.success("Files uploaded successfully.");
            } else {
                toastr.error((result && result.message) || "Unable to upload files.");
            }
        } catch (error) {
            toastr.error("Unable to upload files.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemove = (fileName) => {
        Swal.fire({
            title: "Remove PDF",
            text: "You are about to permanently remove " + fileName,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, remove it",
            cancelButtonText: "No",
            customClass: {
                container:
                    window.localStorage.getItem("appTheme") === "Dark" &&
                    /clinic|lims/.test(window.location.pathname.split("/")[1]) &&
                    "dark-swal",
                cancelButton: "order-1",
                confirmButton: "order-2"
            }
        }).then(async (confirmation) => {
            if (!confirmation.isConfirmed) {
                return;
            }

            setDeletingFile(fileName);
            try {
                const result = await removeRequisitionFile(orderId, fileName);
                if (result && result.status === 200 && Array.isArray(result.requisition_pdf_paths)) {
                    updateFiles(result.requisition_pdf_paths);
                    toastr.success("PDF removed successfully.");
                } else {
                    toastr.error((result && result.message) || "Unable to remove PDF.");
                }
            } catch (error) {
                toastr.error("Unable to remove PDF.");
            } finally {
                setDeletingFile("");
            }
        });
    };

    const getFileUrl = (fileName) =>
        `${serviceConstants.API_HOST_NAME}/files/requisitionPDF/${encodeURIComponent(
            requisitionNo
        )}/${encodeURIComponent(fileName)}?token=${getUserAuthToken()}&isAPI=1`;

    if (!isOpen) {
        return null;
    }

    return (
        <div>
            <h6>Attached PDFs</h6>
            {requisitionPdfPaths && requisitionPdfPaths.length ? (
                <div className="list-group mb-4">
                    {requisitionPdfPaths.map((fileName) => (
                        <div
                            className="list-group-item d-flex align-items-center justify-content-between"
                            key={fileName}
                        >
                            <a href={getFileUrl(fileName)} target="_blank" rel="noreferrer" className="pdf-link">
                                <i className="fa fa-file-pdf-o mr-2"></i>
                                {fileName}
                            </a>
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                disabled={Boolean(deletingFile) || isUploading}
                                onClick={() => handleRemove(fileName)}
                            >
                                {deletingFile === fileName ? "Removing..." : "Remove"}
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-muted">No attached PDFs.</p>
            )}

            <input type="file" multiple accept=".pdf,application/pdf" onChange={handleFileChange} className="mb-3" />
            <div className="d-flex justify-content-between">
                <button
                    type="button"
                    onClick={handleUpload}
                    className="btn btn-info"
                    disabled={isUploading || Boolean(deletingFile)}
                >
                    {isUploading ? "Uploading..." : "Upload"}
                </button>
                <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isUploading}>
                    Close
                </button>
            </div>
        </div>
    );
};

export default UploadFileModel;
